import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { ActionIcon, Avatar, Menu, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import Empty from 'src/components/Feedback/Empty/Empty'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_NOTE_TEMPLATE,
  GET_NOTE_TEMPLATES,
  GET_NOTE_TEMPLATES_WITH_CREATOR,
} from 'src/graphql/noteTemplates.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import { buildFullName } from 'src/lib/formatters'

import CreateNoteTemplateModal from './components/CreateNoteTemplateModal'

const DEFAULT_RESULTS_PER_PAGE = 20
const ORDER_BY = { field: 'title', direction: 'asc' }

// The composer picker reads the slim query, so both are refetched.
const REFETCH_QUERIES = [GET_NOTE_TEMPLATES_WITH_CREATOR, GET_NOTE_TEMPLATES]

interface NoteTemplateRow {
  noteTemplateId: string
  title: string
  content: string
  createdAt: string
  updatedAt: string
  createdBy?: { employeeId: string; firstName?: string; lastName?: string }
}

const NoteTemplateTab = () => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<NoteTemplateRow | null>(null)
  const [editing, setEditing] = useState(false)
  const [createModalOpened, createModalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_NOTE_TEMPLATES_WITH_CREATOR, {
    variables: {
      pageInfo: { limit: DEFAULT_RESULTS_PER_PAGE, offset: 0 },
      orderBy: ORDER_BY,
      search: debouncedSearchTerm || undefined,
    },
    onError,
  })

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({
        pageInfo: { limit, offset },
        orderBy: ORDER_BY,
        search: debouncedSearchTerm || undefined,
      })
    },
    [refetch, debouncedSearchTerm]
  )

  const [deleteNoteTemplate] = useMutation(DELETE_NOTE_TEMPLATE, {
    refetchQueries: REFETCH_QUERIES,
    onCompleted: () => {
      toast('Your note template was deleted successfully', 'success')
    },
    onError,
  })

  const onEdit = useCallback(
    (template: NoteTemplateRow) => {
      setSelectedTemplate(template)
      setEditing(true)
      createModalHandlers.open()
    },
    [createModalHandlers]
  )

  const onCreate = () => {
    setSelectedTemplate(null)
    setEditing(false)
    createModalHandlers.open()
  }

  const onDelete = () => {
    if (!selectedTemplate) return
    deleteNoteTemplate({
      variables: { noteTemplateId: selectedTemplate.noteTemplateId },
    })
  }

  useEffect(() => {
    if (!createModalOpened) {
      setSelectedTemplate(null)
    }
  }, [createModalOpened])

  const templates: NoteTemplateRow[] = data?.noteTemplates?.items ?? []
  const totalCount: number = data?.noteTemplates?.totalCount ?? 0

  const columns = useMemo(
    () => [
      {
        key: 'noteTemplateId',
        label: 'ID',
        width: 6,
        render: (template: NoteTemplateRow) => (
          <Tooltip label={template.noteTemplateId} withArrow>
            <ActionIcon
              variant="subtle"
              size={14}
              color="gray"
              onClick={(e) => {
                e.stopPropagation()
                navigator?.clipboard?.writeText?.(template.noteTemplateId)
              }}
            >
              <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        key: 'title',
        label: 'Template Name',
        width: 24,
        render: (template: NoteTemplateRow) => template.title,
      },
      {
        key: 'content',
        label: 'Note',
        width: 30,
        render: (template: NoteTemplateRow) => (
          <Tooltip
            label={template.content}
            withArrow
            multiline
            maw={400}
            openDelay={300}
          >
            <span className="line-clamp-1 whitespace-pre-line">
              {template.content}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 10,
        render: (template: NoteTemplateRow) =>
          template.createdBy ? (
            <Tooltip label={buildFullName(template.createdBy)} withArrow>
              <Avatar color="blue" size="sm" radius="sm">
                {template.createdBy.firstName?.charAt(0)}
                {template.createdBy.lastName?.charAt(0)}
              </Avatar>
            </Tooltip>
          ) : (
            'N/A'
          ),
      },
      {
        key: 'updatedAt',
        label: 'Updated Date',
        render: (template: NoteTemplateRow) => (
          <Tooltip
            label={dayjs(template.updatedAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.updatedAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 10,
        render: (template: NoteTemplateRow) => (
          <div className="flex flex-row justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  aria-label={`Actions for ${template.title}`}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(template)
                  }}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedTemplate(template)
                    deleteModalHandlers.open()
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [deleteModalHandlers, onEdit]
  )

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Note Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{totalCount}</span>
            <span> note templates</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton variant="primary" onClick={onCreate}>
            Create Template
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={templates}
          enableColumnResize={false}
          enablePagination
          loading={loading}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          onRowClick={(_idx, row) => onEdit(row)}
          defaultResultsPerPage={DEFAULT_RESULTS_PER_PAGE}
          emptyStateMessage={
            <Empty
              title="No templates found"
              subtitle="Add one to get started"
            />
          }
        />
      </div>
      <CreateNoteTemplateModal
        noteTemplate={selectedTemplate ?? undefined}
        edit={editing}
        isOpen={createModalOpened}
        onClose={createModalHandlers.close}
        refetchQueries={REFETCH_QUERIES}
      />
      <DeleteModal
        title={`Are you sure you want to delete the template ${selectedTemplate?.title}?`}
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default NoteTemplateTab
